import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { publicService } from '../services/api';
import { motion, useInView, AnimatePresence } from 'framer-motion';
import {
  Car as CarIcon, MapPin, Phone, Clock, Mail, Instagram,
  X, ChevronLeft, ChevronRight, Fuel, Settings, Gauge,
  Calendar, Palette, Search, SlidersHorizontal, ExternalLink,
  MessageCircle, DoorOpen
} from 'lucide-react';

/* ═══ DESIGN TOKENS ═══ */
const T = {
  bg: '#050A30',
  bgAlt: '#0A1240',
  bgDeep: '#020818',
  cyan: '#00FFFF',
  blue: '#0000FF',
  white: '#FFFFFF',
  muted: '#A0A8C8',
  light: '#E8E8F0',
  cardBg: 'rgba(255,255,255,0.04)',
  cardBorder: 'rgba(0,255,255,0.12)',
  font: {
    display: "'Barlow Condensed', sans-serif",
    heading: "'Barlow', sans-serif",
    body: "'Inter', sans-serif",
  }
};

const gridBg = {
  backgroundImage: `linear-gradient(rgba(0,255,255,0.03) 1px, transparent 1px), linear-gradient(90deg, rgba(0,255,255,0.03) 1px, transparent 1px)`,
  backgroundSize: '60px 60px',
};

/* ═══ HELPERS ═══ */
const fmt = (price, currency = 'ARS') => {
  if (!price || price === 0) return 'Consultar';
  return new Intl.NumberFormat('es-AR', { style: 'currency', currency, maximumFractionDigits: 0 }).format(price);
};
const fmtKm = (km) => km ? new Intl.NumberFormat('es-AR').format(km) + ' km' : '-';
const transLabel = (t) => ({ manual: 'Manual', automatic: 'Automático' }[t] || t || '-');
const fuelLabel = (f) => ({ nafta: 'Nafta', diesel: 'Diesel', gnc: 'GNC', electrico: 'Eléctrico', hibrido: 'Híbrido' }[f] || f || '-');
const waMsg = (make, model, year) => encodeURIComponent(`Hola, vi el ${make} ${model} ${year} en AutoFlow y me interesa más información`);
const waLink = (phone, make, model, year) => `https://wa.me/${(phone || '').replace(/\D/g, '')}?text=${waMsg(make || '', model || '', year || '')}`;

/* ═══ REVEAL WRAPPER ═══ */
function Reveal({ children, className = "", delay = 0 }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-40px" });
  return (
    <motion.div ref={ref} initial={{ opacity: 0, y: 24 }} animate={inView ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.45, delay, ease: [0.22, 1, 0.36, 1] }} className={className}>
      {children}
    </motion.div>
  );
}

/* ═══ CAR PLACEHOLDER SVG ═══ */
function CarPlaceholder({ accent = T.cyan }) {
  return (
    <svg viewBox="0 0 200 120" fill="none" className="w-24 h-16 opacity-30">
      <path d="M30 85 L50 55 L80 45 L140 45 L165 55 L180 70 L185 80 L185 90 L175 95 L168 88 L158 88 L152 95 L68 95 L62 88 L52 88 L46 95 L30 95 Z" stroke={accent} strokeWidth="1.5" fill="rgba(0,255,255,0.05)" />
      <circle cx="65" cy="95" r="10" stroke={accent} strokeWidth="1.5" fill="none" />
      <circle cx="155" cy="95" r="10" stroke={accent} strokeWidth="1.5" fill="none" />
    </svg>
  );
}

/* ═══ STATUS BADGE ═══ */
function StatusBadge({ status }) {
  const config = {
    published: { label: 'Disponible', bg: 'rgba(34,197,94,0.15)', border: 'rgba(34,197,94,0.3)', color: '#22C55E' },
    reserved: { label: 'Reservado', bg: 'rgba(234,179,8,0.15)', border: 'rgba(234,179,8,0.3)', color: '#EAB308' },
    sold: { label: 'Vendido', bg: 'rgba(239,68,68,0.15)', border: 'rgba(239,68,68,0.3)', color: '#EF4444' },
  };
  const c = config[status] || config.published;
  return (
    <span className="absolute top-3 left-3 text-xs font-semibold px-2.5 py-1 rounded-full z-10" style={{ background: c.bg, border: `1px solid ${c.border}`, color: c.color, fontFamily: T.font.heading }}>
      {c.label}
    </span>
  );
}

/* ═══ MAIN COMPONENT ═══ */
const PublicAgencyPage = () => {
  const { slug } = useParams();
  const [agency, setAgency] = useState(null);
  const [vehicles, setVehicles] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [loading, setLoading] = useState(true);
  const [scrolled, setScrolled] = useState(false);

  // Filters
  const [search, setSearch] = useState('');
  const [filterMake, setFilterMake] = useState('');
  const [filterYear, setFilterYear] = useState('');
  const [priceMin, setPriceMin] = useState('');
  const [priceMax, setPriceMax] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const hasFilters = search || filterMake || filterYear || priceMin || priceMax;

  // Pagination
  const [visibleCount, setVisibleCount] = useState(12);

  // Modal
  const [selected, setSelected] = useState(null);
  const [imgIdx, setImgIdx] = useState(0);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const [a, v] = await Promise.all([
          publicService.getAgencyBySlug(slug),
          publicService.getAgencyVehicles(slug)
        ]);
        setAgency(a);
        setVehicles(v);
      } catch { /* handled by !agency state */ }
      finally { setLoading(false); }
    })();
  }, [slug]);

  // Apply filters
  useEffect(() => {
    let r = [...vehicles];
    if (search) {
      const s = search.toLowerCase();
      r = r.filter(v => `${v.vehicle?.make} ${v.vehicle?.model} ${v.vehicle?.trim}`.toLowerCase().includes(s));
    }
    if (filterMake) r = r.filter(v => v.vehicle?.make === filterMake);
    if (filterYear) r = r.filter(v => String(v.vehicle?.year) === filterYear);
    if (priceMin) r = r.filter(v => (v.pricing?.price || 0) >= Number(priceMin));
    if (priceMax) r = r.filter(v => (v.pricing?.price || 0) <= Number(priceMax));
    setFiltered(r);
    setVisibleCount(12);
  }, [vehicles, search, filterMake, filterYear, priceMin, priceMax]);

  const clearFilters = () => { setSearch(''); setFilterMake(''); setFilterYear(''); setPriceMin(''); setPriceMax(''); };

  const makes = [...new Set(vehicles.map(v => v.vehicle?.make).filter(Boolean))].sort();
  const years = [...new Set(vehicles.map(v => v.vehicle?.year).filter(Boolean))].sort((a, b) => b - a);

  const getImages = (v) => {
    const imgs = [];
    if (v?.media?.coverUrl) imgs.push(v.media.coverUrl);
    if (v?.media?.images?.length) imgs.push(...v.media.images);
    return imgs;
  };

  const openModal = (v) => { setSelected(v); setImgIdx(0); document.body.style.overflow = 'hidden'; };
  const closeModal = () => { setSelected(null); setImgIdx(0); document.body.style.overflow = ''; };

  // Track vehicle view when detail modal opens
  useEffect(() => {
    if (selected?.id) {
      publicService.trackVehicleView(selected.id);
    }
  }, [selected?.id]);

  // Keyboard nav
  useEffect(() => {
    if (!selected) return;
    const handler = (e) => {
      const imgs = getImages(selected);
      if (e.key === 'Escape') closeModal();
      if (e.key === 'ArrowRight') setImgIdx(p => (p + 1) % imgs.length);
      if (e.key === 'ArrowLeft') setImgIdx(p => (p - 1 + imgs.length) % imgs.length);
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [selected]);

  const banner = agency?.bannerUrl || agency?.media?.bannerUrl;
  const logo = agency?.logoUrl || agency?.media?.logoUrl;
  const phone = agency?.phonePublic || agency?.phone || '';
  const phoneSecondary = agency?.phoneSecondary || '';
  const agencyColor = agency?.color || T.cyan;

  // ═══ LOADING ═══
  if (loading) return (
    <div className="min-h-screen flex items-center justify-center" style={{ background: T.bg }}>
      <div className="w-10 h-10 border-2 border-t-transparent rounded-full animate-spin" style={{ borderColor: `${T.cyan} transparent ${T.cyan} ${T.cyan}` }} />
    </div>
  );

  // ═══ NOT FOUND ═══
  if (!agency) return (
    <div className="min-h-screen flex flex-col items-center justify-center gap-4" style={{ background: T.bg, color: T.white, fontFamily: T.font.body }}>
      <CarPlaceholder />
      <h2 className="text-2xl font-bold" style={{ fontFamily: T.font.display }}>Agencia no encontrada</h2>
      <p style={{ color: T.muted }}>La agencia que buscás no existe o no está disponible.</p>
      <Link to="/" className="text-sm mt-4 px-6 py-2.5 rounded-full" style={{ border: `1px solid ${T.cyan}`, color: T.cyan }}>Volver al inicio</Link>
    </div>
  );

  return (
    <div style={{ background: T.bg, fontFamily: T.font.body, color: T.white, '--agency-color': agencyColor }} className="min-h-screen" data-testid="public-agency-page">

      {/* ═══ SEO HEAD ═══ */}
      <Helmet>
        <title>{agency.displayName || agency.name} | Autos en venta — AutoFlow</title>
        <meta name="description" content={`Encontrá los mejores autos en ${agency.displayName || agency.name}. Catálogo actualizado, precios y atención inmediata.${agency.addressText ? ` En ${agency.addressText}.` : ''}`} />
        <meta property="og:title" content={`${agency.displayName || agency.name} | AutoFlow`} />
        <meta property="og:description" content={agency.description || `Agencia de autos verificada en AutoFlow. Catálogo actualizado y atención inmediata.`} />
        <meta property="og:image" content={agency.logoUrl || '/logo.png'} />
        <meta property="og:url" content={`https://autoflowapp.net/a/${slug}`} />
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={`${agency.displayName || agency.name} | AutoFlow`} />
        <meta name="twitter:description" content={agency.description || `Agencia de autos verificada en AutoFlow.`} />
        <meta name="twitter:image" content={agency.logoUrl || '/logo.png'} />
      </Helmet>

      {/* ═══ HEADER ═══ */}
      <header
        className="fixed top-0 left-0 right-0 z-50 transition-all duration-300"
        style={{
          background: scrolled ? 'rgba(5,10,48,0.9)' : 'transparent',
          backdropFilter: scrolled ? 'blur(20px)' : 'none',
          borderBottom: scrolled ? '1px solid rgba(0,255,255,0.06)' : 'none',
        }}
        data-testid="agency-header"
      >
        <div className="max-w-7xl mx-auto px-4 md:px-8 flex items-center justify-between h-14 md:h-16">
          <Link to="/" className="flex items-center gap-2 opacity-60 hover:opacity-100 transition-opacity" data-testid="powered-by-link">
            <img src="/logo.png" alt="AutoFlow" className="h-7 w-auto rounded" />
            <span className="text-xs" style={{ color: T.muted }}>Powered by</span>
            <span className="text-sm font-bold" style={{ fontFamily: T.font.display }}>Auto<span style={{ color: T.cyan }}>Flow</span></span>
          </Link>

          <h2 className="hidden sm:block text-base font-bold tracking-tight" style={{ fontFamily: T.font.display }} data-testid="header-agency-name">
            {agency.displayName || agency.name}
          </h2>

          {phone && (
            <a href={waLink(phone, '', '', '')} target="_blank" rel="noopener noreferrer"
              className="flex items-center gap-2 px-4 py-2 rounded-full text-xs font-semibold transition-all duration-300"
              style={{ background: '#25D366', color: T.white, fontFamily: T.font.heading }}
              data-testid="header-whatsapp"
            >
              <MessageCircle size={14} />
              <span className="hidden sm:inline">Consultar por WhatsApp</span>
              <span className="sm:hidden">WhatsApp</span>
            </a>
          )}
        </div>
      </header>

      {/* ═══ HERO ═══ */}
      <section className="relative pt-14 md:pt-16" data-testid="agency-hero">
        <div className="relative overflow-hidden" style={{ height: 'clamp(280px, 40vw, 400px)' }}>
          {banner ? (
            <>
              <img src={banner} alt="" className="absolute inset-0 w-full h-full object-cover" />
              <div className="absolute inset-0" style={{ background: `linear-gradient(to bottom, ${T.bg}99, ${T.bg}DD)` }} />
            </>
          ) : (
            <div className="absolute inset-0" style={{ ...gridBg, background: `linear-gradient(160deg, ${T.bg} 0%, ${T.bgAlt} 100%)` }} />
          )}

          <div className="absolute inset-0 flex items-center">
            <div className="max-w-7xl mx-auto px-4 md:px-8 w-full">
              <Reveal>
                <div className="flex items-center gap-5 mb-5">
                  {logo && (
                    <div className="w-16 h-16 md:w-20 md:h-20 rounded-xl overflow-hidden shrink-0 ring-1" style={{ ringColor: T.cardBorder }}>
                      <img src={logo} alt="" className="w-full h-full object-cover" />
                    </div>
                  )}
                  <div>
                    <span className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.2em] px-3 py-1 rounded-full mb-2" style={{ fontFamily: T.font.display, fontWeight: 600, background: 'rgba(0,255,255,0.08)', border: `1px solid rgba(0,255,255,0.2)`, color: T.cyan }}>
                      <span style={{ fontSize: '8px' }}>&#10022;</span> Agencia verificada AutoFlow
                    </span>
                    <h1 className="text-3xl sm:text-4xl lg:text-5xl tracking-tighter leading-none" style={{ fontFamily: T.font.display, fontWeight: 800 }} data-testid="agency-name-hero">
                      {agency.displayName || agency.name}
                    </h1>
                  </div>
                </div>
              </Reveal>

              <Reveal delay={0.08}>
                {agency.description && <p className="text-sm md:text-base max-w-xl mb-4" style={{ color: T.muted }}>{agency.description}</p>}
                <div className="flex flex-wrap items-center gap-x-5 gap-y-2 text-xs" style={{ color: T.muted }}>
                  {agency.addressText && <span className="flex items-center gap-1.5"><MapPin size={13} style={{ color: T.cyan }} />{agency.addressText}</span>}
                  {phone && <span className="flex items-center gap-1.5"><Phone size={13} style={{ color: T.cyan }} />{phone}</span>}
                  {phoneSecondary && <span className="flex items-center gap-1.5"><Phone size={13} style={{ color: T.cyan }} />{phoneSecondary}</span>}
                  {agency.hours && <span className="flex items-center gap-1.5"><Clock size={13} style={{ color: T.cyan }} />{agency.hours}</span>}
                </div>
              </Reveal>

              <Reveal delay={0.14}>
                <div className="flex gap-3 mt-5">
                  <button onClick={() => document.getElementById('catalogo')?.scrollIntoView({ behavior: 'smooth' })}
                    className="px-5 py-2.5 rounded-full text-xs font-semibold transition-all"
                    style={{ fontFamily: T.font.heading, background: T.cyan, color: T.bg }}
                    data-testid="hero-cta-catalog"
                  >Ver catálogo</button>
                  {phone && (
                    <a href={waLink(phone, '', '', '')} target="_blank" rel="noopener noreferrer"
                      className="px-5 py-2.5 rounded-full text-xs font-semibold transition-all"
                      style={{ fontFamily: T.font.heading, border: '1px solid rgba(255,255,255,0.2)', color: T.white }}
                      data-testid="hero-cta-whatsapp"
                    >Escribinos por WhatsApp</a>
                  )}
                </div>
              </Reveal>
            </div>
          </div>
        </div>
      </section>

      {/* ═══ INFO CARD ═══ */}
      {(agency.addressText || phone || agency.hours || agency.emailPublic) && (
        <section className="py-8" data-testid="agency-info-section">
          <div className="max-w-7xl mx-auto px-4 md:px-8">
            <Reveal>
              <div className="rounded-2xl p-5 md:p-6 grid grid-cols-2 md:grid-cols-4 gap-4" style={{ background: T.cardBg, border: `1px solid ${T.cardBorder}`, backdropFilter: 'blur(12px)' }}>
                {agency.addressText && (
                  <div className="flex items-start gap-3">
                    <div className="w-9 h-9 rounded-lg flex items-center justify-center shrink-0" style={{ background: 'rgba(0,255,255,0.08)' }}>
                      <MapPin size={16} style={{ color: T.cyan }} />
                    </div>
                    <div>
                      <div className="text-[10px] uppercase tracking-widest mb-0.5" style={{ fontFamily: T.font.display, fontWeight: 600, color: T.muted }}>Dirección</div>
                      <div className="text-xs leading-relaxed" style={{ color: T.light }}>{agency.addressText}</div>
                      <a href={`https://maps.google.com/?q=${encodeURIComponent(agency.addressText)}`} target="_blank" rel="noopener noreferrer" className="text-[10px] flex items-center gap-1 mt-1" style={{ color: T.cyan }}>
                        Ver en Google Maps <ExternalLink size={9} />
                      </a>
                    </div>
                  </div>
                )}
                {phone && (
                  <div className="flex items-start gap-3">
                    <div className="w-9 h-9 rounded-lg flex items-center justify-center shrink-0" style={{ background: 'rgba(0,255,255,0.08)' }}>
                      <Phone size={16} style={{ color: T.cyan }} />
                    </div>
                    <div>
                      <div className="text-[10px] uppercase tracking-widest mb-0.5" style={{ fontFamily: T.font.display, fontWeight: 600, color: T.muted }}>Teléfono</div>
                      <a href={`tel:${phone}`} className="text-xs" style={{ color: T.light }}>{phone}</a>
                      {phoneSecondary && (
                        <a href={`tel:${phoneSecondary}`} className="text-xs block mt-0.5" style={{ color: T.light }}>{phoneSecondary}</a>
                      )}
                    </div>
                  </div>
                )}
                {agency.hours && (
                  <div className="flex items-start gap-3">
                    <div className="w-9 h-9 rounded-lg flex items-center justify-center shrink-0" style={{ background: 'rgba(0,255,255,0.08)' }}>
                      <Clock size={16} style={{ color: T.cyan }} />
                    </div>
                    <div>
                      <div className="text-[10px] uppercase tracking-widest mb-0.5" style={{ fontFamily: T.font.display, fontWeight: 600, color: T.muted }}>Horarios</div>
                      <div className="text-xs" style={{ color: T.light }}>{agency.hours}</div>
                    </div>
                  </div>
                )}
                {(agency.emailPublic || agency.social?.instagram) && (
                  <div className="flex items-start gap-3">
                    <div className="w-9 h-9 rounded-lg flex items-center justify-center shrink-0" style={{ background: 'rgba(0,255,255,0.08)' }}>
                      {agency.emailPublic ? <Mail size={16} style={{ color: T.cyan }} /> : <Instagram size={16} style={{ color: T.cyan }} />}
                    </div>
                    <div>
                      <div className="text-[10px] uppercase tracking-widest mb-0.5" style={{ fontFamily: T.font.display, fontWeight: 600, color: T.muted }}>Contacto</div>
                      {agency.emailPublic && <a href={`mailto:${agency.emailPublic}`} className="text-xs block" style={{ color: T.light }}>{agency.emailPublic}</a>}
                      {agency.social?.instagram && <a href={agency.social.instagram} target="_blank" rel="noopener noreferrer" className="text-xs flex items-center gap-1" style={{ color: T.cyan }}>Instagram <ExternalLink size={9} /></a>}
                    </div>
                  </div>
                )}
              </div>
            </Reveal>
          </div>
        </section>
      )}

      {/* ═══ CATALOG ═══ */}
      <section id="catalogo" className="py-10 md:py-14" data-testid="catalog-section">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <Reveal>
            <div className="flex items-end justify-between mb-6">
              <div>
                <span className="text-[10px] uppercase tracking-[0.25em] mb-1 block" style={{ fontFamily: T.font.display, fontWeight: 600, color: T.cyan }}>Catálogo</span>
                <h2 className="text-2xl sm:text-3xl tracking-tight" style={{ fontFamily: T.font.display, fontWeight: 800 }}>
                  Autos disponibles
                </h2>
              </div>
              <span className="text-xs" style={{ color: T.muted }}>{filtered.length} vehículo{filtered.length !== 1 ? 's' : ''}</span>
            </div>
          </Reveal>

          {/* Filters Bar */}
          <Reveal delay={0.05}>
            <div className="rounded-xl p-4 mb-6" style={{ background: T.cardBg, border: `1px solid ${T.cardBorder}` }} data-testid="filters-bar">
              <div className="flex flex-wrap items-center gap-3">
                {/* Search */}
                <div className="relative flex-1 min-w-[200px]">
                  <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: T.muted }} />
                  <input type="text" value={search} onChange={e => setSearch(e.target.value)} placeholder="Buscar marca o modelo..."
                    className="w-full pl-9 pr-3 py-2.5 rounded-lg text-xs outline-none transition-all"
                    style={{ background: 'rgba(255,255,255,0.04)', border: `1px solid ${T.cardBorder}`, color: T.white, fontFamily: T.font.body }}
                    onFocus={e => e.currentTarget.style.borderColor = T.cyan}
                    onBlur={e => e.currentTarget.style.borderColor = T.cardBorder}
                    data-testid="filter-search"
                  />
                </div>

                {/* Toggle advanced */}
                <button onClick={() => setShowFilters(!showFilters)} className="flex items-center gap-1.5 px-3 py-2.5 rounded-lg text-xs"
                  style={{ background: showFilters ? 'rgba(0,255,255,0.1)' : 'rgba(255,255,255,0.04)', border: `1px solid ${showFilters ? 'rgba(0,255,255,0.3)' : T.cardBorder}`, color: showFilters ? T.cyan : T.muted, fontFamily: T.font.heading }}
                  data-testid="filter-toggle"
                >
                  <SlidersHorizontal size={13} /> Filtros
                </button>

                {hasFilters && (
                  <button onClick={clearFilters} className="text-xs px-3 py-2.5 rounded-lg transition-colors"
                    style={{ color: T.cyan, border: `1px solid rgba(0,255,255,0.2)` }}
                    data-testid="filter-clear"
                  >Limpiar</button>
                )}
              </div>

              {/* Advanced Filters */}
              <AnimatePresence>
                {showFilters && (
                  <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.25 }} className="overflow-hidden">
                    <div className="flex flex-wrap gap-3 mt-3 pt-3" style={{ borderTop: '1px solid rgba(255,255,255,0.06)' }}>
                      <select value={filterMake} onChange={e => setFilterMake(e.target.value)}
                        className="px-3 py-2.5 rounded-lg text-xs outline-none min-w-[140px]"
                        style={{ background: 'rgba(255,255,255,0.04)', border: `1px solid ${T.cardBorder}`, color: T.white, fontFamily: T.font.body }}
                        data-testid="filter-make"
                      >
                        <option value="">Todas las marcas</option>
                        {makes.map(m => <option key={m} value={m}>{m}</option>)}
                      </select>
                      <select value={filterYear} onChange={e => setFilterYear(e.target.value)}
                        className="px-3 py-2.5 rounded-lg text-xs outline-none min-w-[110px]"
                        style={{ background: 'rgba(255,255,255,0.04)', border: `1px solid ${T.cardBorder}`, color: T.white, fontFamily: T.font.body }}
                        data-testid="filter-year"
                      >
                        <option value="">Todos los años</option>
                        {years.map(y => <option key={y} value={String(y)}>{y}</option>)}
                      </select>
                      <input type="number" value={priceMin} onChange={e => setPriceMin(e.target.value)} placeholder="Precio desde $"
                        className="px-3 py-2.5 rounded-lg text-xs outline-none w-[130px]"
                        style={{ background: 'rgba(255,255,255,0.04)', border: `1px solid ${T.cardBorder}`, color: T.white, fontFamily: T.font.body }}
                        data-testid="filter-price-min"
                      />
                      <input type="number" value={priceMax} onChange={e => setPriceMax(e.target.value)} placeholder="Precio hasta $"
                        className="px-3 py-2.5 rounded-lg text-xs outline-none w-[130px]"
                        style={{ background: 'rgba(255,255,255,0.04)', border: `1px solid ${T.cardBorder}`, color: T.white, fontFamily: T.font.body }}
                        data-testid="filter-price-max"
                      />
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </Reveal>

          {/* Vehicle Grid */}
          {filtered.length === 0 ? (
            <div className="text-center py-20">
              <CarPlaceholder />
              <h3 className="text-xl font-bold mt-4 mb-2" style={{ fontFamily: T.font.display }}>
                {vehicles.length === 0 ? 'Próximamente nuevos modelos disponibles' : 'Sin resultados para esos filtros'}
              </h3>
              <p className="text-sm" style={{ color: T.muted }}>
                {vehicles.length === 0 ? 'Esta agencia está preparando su catálogo.' : 'Probá con otros filtros de búsqueda.'}
              </p>
              {hasFilters && <button onClick={clearFilters} className="mt-4 text-xs px-5 py-2 rounded-full" style={{ border: `1px solid ${T.cyan}`, color: T.cyan }}>Limpiar filtros</button>}
            </div>
          ) : (
            <>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
                {filtered.slice(0, visibleCount).map((v, i) => {
                  const imgs = getImages(v);
                  const make = v.vehicle?.make || '';
                  const model = v.vehicle?.model || '';
                  const year = v.vehicle?.year || '';
                  return (
                    <Reveal key={v.id || i} delay={Math.min(i * 0.04, 0.3)}>
                      <div
                        className="rounded-xl overflow-hidden cursor-pointer group transition-all duration-300"
                        style={{ background: T.cardBg, border: `1px solid ${T.cardBorder}` }}
                        onClick={() => openModal(v)}
                        onMouseEnter={e => { e.currentTarget.style.borderColor = 'rgba(0,255,255,0.3)'; e.currentTarget.style.boxShadow = '0 0 24px rgba(0,255,255,0.06)'; e.currentTarget.style.transform = 'translateY(-4px)'; }}
                        onMouseLeave={e => { e.currentTarget.style.borderColor = T.cardBorder; e.currentTarget.style.boxShadow = 'none'; e.currentTarget.style.transform = 'translateY(0)'; }}
                        data-testid={`vehicle-card-${v.id || i}`}
                      >
                        {/* Image */}
                        <div className="relative h-52 overflow-hidden" style={{ background: T.bgAlt }}>
                          <StatusBadge status={v.status} />
                          {imgs.length > 0 ? (
                            <img src={imgs[0]} alt={`${make} ${model}`} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" loading="lazy" />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center"><CarPlaceholder /></div>
                          )}
                          {imgs.length > 1 && (
                            <span className="absolute bottom-2 right-2 text-[10px] px-2 py-0.5 rounded-full" style={{ background: 'rgba(0,0,0,0.6)', color: T.muted }}>{imgs.length} fotos</span>
                          )}
                        </div>

                        {/* Content */}
                        <div className="p-4">
                          <h3 className="text-lg font-bold tracking-tight mb-1" style={{ fontFamily: T.font.display, fontWeight: 700 }}>
                            {make} {model}
                          </h3>
                          <div className="flex flex-wrap gap-2 mb-3 text-[10px]" style={{ color: T.muted }}>
                            {year && <span className="flex items-center gap-1"><Calendar size={10} />{year}</span>}
                            {v.vehicle?.kilometers && <span className="flex items-center gap-1"><Gauge size={10} />{fmtKm(v.vehicle.kilometers)}</span>}
                            {v.vehicle?.transmission && <span className="flex items-center gap-1"><Settings size={10} />{transLabel(v.vehicle.transmission)}</span>}
                          </div>
                          <div className="text-xl font-bold" style={{ fontFamily: T.font.heading, fontWeight: 800, color: T.cyan }} data-testid={`vehicle-price-${v.id || i}`}>
                            {fmt(v.pricing?.price, v.pricing?.currency)}
                          </div>

                          {/* Actions */}
                          <div className="flex gap-2 mt-3">
                            <button className="flex-1 text-xs py-2 rounded-lg font-semibold transition-colors"
                              style={{ background: 'rgba(0,255,255,0.08)', border: `1px solid rgba(0,255,255,0.2)`, color: T.cyan, fontFamily: T.font.heading }}
                              data-testid={`vehicle-detail-btn-${v.id || i}`}
                            >Ver detalles</button>
                            {phone && (
                              <a href={waLink(phone, make, model, year)} target="_blank" rel="noopener noreferrer"
                                onClick={e => e.stopPropagation()}
                                className="flex items-center justify-center gap-1 text-xs py-2 px-3 rounded-lg font-semibold"
                                style={{ background: 'rgba(37,211,102,0.15)', border: '1px solid rgba(37,211,102,0.3)', color: '#25D366', fontFamily: T.font.heading }}
                                data-testid={`vehicle-wa-btn-${v.id || i}`}
                              ><MessageCircle size={12} />WhatsApp</a>
                            )}
                          </div>
                        </div>
                      </div>
                    </Reveal>
                  );
                })}
              </div>

              {/* Load More */}
              {visibleCount < filtered.length && (
                <div className="text-center mt-8">
                  <button onClick={() => setVisibleCount(p => p + 12)} className="px-8 py-3 rounded-full text-sm font-semibold transition-all"
                    style={{ border: `1px solid ${T.cyan}`, color: T.cyan, fontFamily: T.font.heading }}
                    data-testid="load-more-btn"
                  >Cargar más ({filtered.length - visibleCount} restantes)</button>
                </div>
              )}
            </>
          )}
        </div>
      </section>

      {/* ═══ VEHICLE DETAIL MODAL ═══ */}
      <AnimatePresence>
        {selected && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.2 }}
            className="fixed inset-0 z-[100] flex items-center justify-center p-4"
            style={{ background: 'rgba(2,8,24,0.92)', backdropFilter: 'blur(8px)' }}
            onClick={closeModal}
            data-testid="vehicle-modal"
          >
            <motion.div initial={{ scale: 0.95, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.95, opacity: 0 }}
              className="w-full max-w-5xl max-h-[92vh] overflow-hidden rounded-2xl flex flex-col md:flex-row"
              style={{ background: T.bgAlt, border: `1px solid ${T.cardBorder}` }}
              onClick={e => e.stopPropagation()}
            >
              {/* Gallery */}
              <div className="w-full md:w-1/2 relative shrink-0" style={{ background: T.bgDeep }}>
                <div className="aspect-[4/3] relative">
                  {getImages(selected).length > 0 ? (
                    <img src={getImages(selected)[imgIdx]} alt="" className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center"><CarPlaceholder /></div>
                  )}
                  {getImages(selected).length > 1 && (
                    <>
                      <button onClick={() => setImgIdx(p => (p - 1 + getImages(selected).length) % getImages(selected).length)}
                        className="absolute left-2 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full flex items-center justify-center"
                        style={{ background: 'rgba(0,0,0,0.5)' }} data-testid="modal-prev-img"><ChevronLeft size={18} /></button>
                      <button onClick={() => setImgIdx(p => (p + 1) % getImages(selected).length)}
                        className="absolute right-2 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full flex items-center justify-center"
                        style={{ background: 'rgba(0,0,0,0.5)' }} data-testid="modal-next-img"><ChevronRight size={18} /></button>
                      <span className="absolute bottom-3 left-1/2 -translate-x-1/2 text-xs px-3 py-1 rounded-full" style={{ background: 'rgba(0,0,0,0.6)' }}>
                        {imgIdx + 1} / {getImages(selected).length}
                      </span>
                    </>
                  )}
                </div>
                {/* Thumbnails */}
                {getImages(selected).length > 1 && (
                  <div className="flex gap-1.5 p-2 overflow-x-auto" style={{ background: T.bgDeep }}>
                    {getImages(selected).map((img, idx) => (
                      <button key={idx} onClick={() => setImgIdx(idx)}
                        className="w-14 h-10 shrink-0 rounded overflow-hidden transition-all"
                        style={{ border: idx === imgIdx ? `2px solid ${T.cyan}` : '2px solid transparent', opacity: idx === imgIdx ? 1 : 0.5 }}
                        data-testid={`modal-thumb-${idx}`}
                      ><img src={img} alt="" className="w-full h-full object-cover" /></button>
                    ))}
                  </div>
                )}
              </div>

              {/* Details */}
              <div className="w-full md:w-1/2 p-6 overflow-y-auto relative">
                <button onClick={closeModal} className="absolute top-4 right-4 w-8 h-8 rounded-full flex items-center justify-center transition-colors"
                  style={{ background: 'rgba(255,255,255,0.05)', color: T.muted }}
                  data-testid="modal-close"><X size={16} /></button>

                <h2 className="text-2xl font-bold tracking-tight mb-1" style={{ fontFamily: T.font.display, fontWeight: 800 }}>
                  {selected.vehicle?.make} {selected.vehicle?.model}
                </h2>
                {selected.vehicle?.trim && <p className="text-sm mb-3" style={{ color: T.muted }}>{selected.vehicle.trim}</p>}

                <div className="flex items-baseline gap-2 mb-6">
                  <span className="text-3xl font-bold" style={{ fontFamily: T.font.heading, fontWeight: 800, color: T.cyan }}>
                    {fmt(selected.pricing?.price, selected.pricing?.currency)}
                  </span>
                  {selected.pricing?.negotiable && (
                    <span className="text-[10px] px-2 py-0.5 rounded-full" style={{ background: 'rgba(34,197,94,0.15)', color: '#22C55E' }}>Negociable</span>
                  )}
                </div>

                {/* Specs Grid */}
                <div className="grid grid-cols-2 gap-2.5 mb-6">
                  {[
                    { icon: Calendar, label: 'Año', val: selected.vehicle?.year },
                    { icon: Gauge, label: 'Kilómetros', val: fmtKm(selected.vehicle?.kilometers) },
                    { icon: Fuel, label: 'Combustible', val: fuelLabel(selected.vehicle?.fuel) },
                    { icon: Settings, label: 'Transmisión', val: transLabel(selected.vehicle?.transmission) },
                    selected.vehicle?.engine?.displacement ? { icon: Settings, label: 'Motor', val: selected.vehicle.engine.displacement } : null,
                    selected.vehicle?.color ? { icon: Palette, label: 'Color', val: selected.vehicle.color } : null,
                    selected.vehicle?.doors ? { icon: DoorOpen, label: 'Puertas', val: selected.vehicle.doors } : null,
                  ].filter(Boolean).map((spec, i) => (
                    <div key={i} className="rounded-lg p-3" style={{ background: 'rgba(255,255,255,0.03)', border: `1px solid rgba(255,255,255,0.05)` }}>
                      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-widest mb-1" style={{ fontFamily: T.font.display, fontWeight: 600, color: T.muted }}>
                        <spec.icon size={11} /> {spec.label}
                      </div>
                      <div className="text-sm font-medium">{spec.val || '-'}</div>
                    </div>
                  ))}
                </div>

                {/* Description */}
                {selected.description && (
                  <div className="mb-6">
                    <h3 className="text-xs uppercase tracking-widest mb-2" style={{ fontFamily: T.font.display, fontWeight: 600, color: T.muted }}>Descripción</h3>
                    <p className="text-sm leading-relaxed whitespace-pre-line" style={{ color: T.light }}>{selected.description}</p>
                  </div>
                )}

                {/* Features */}
                {selected.features?.length > 0 && (
                  <div className="mb-6">
                    <h3 className="text-xs uppercase tracking-widest mb-2" style={{ fontFamily: T.font.display, fontWeight: 600, color: T.muted }}>Características</h3>
                    <div className="flex flex-wrap gap-1.5">
                      {selected.features.map((f, i) => <span key={i} className="text-xs px-2.5 py-1 rounded-full" style={{ background: 'rgba(0,255,255,0.06)', border: `1px solid rgba(0,255,255,0.12)`, color: T.light }}>{f}</span>)}
                    </div>
                  </div>
                )}

                {/* CTAs */}
                <div className="space-y-2 mt-auto">
                  {phone && (
                    <a href={waLink(phone, selected.vehicle?.make, selected.vehicle?.model, selected.vehicle?.year)} target="_blank" rel="noopener noreferrer" className="w-full flex items-center justify-center gap-2 py-3 rounded-full text-sm font-semibold transition-all"
                      style={{ background: '#25D366', color: T.white, fontFamily: T.font.heading }}
                      data-testid="modal-whatsapp"
                    ><MessageCircle size={16} />Consultar por WhatsApp</a>
                  )}
                  {phone && (
                    <a href={`tel:${phone}`} className="w-full flex items-center justify-center gap-2 py-3 rounded-full text-sm font-semibold transition-all"
                      style={{ border: `1px solid rgba(255,255,255,0.15)`, color: T.white, fontFamily: T.font.heading }}
                      data-testid="modal-call"
                    ><Phone size={16} />Llamar a la agencia</a>
                  )}
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ═══ FOOTER ═══ */}
      <footer className="py-8 mt-6" style={{ background: T.bgDeep, borderTop: '1px solid rgba(255,255,255,0.05)' }} data-testid="agency-footer">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              {logo && <img src={logo} alt="" className="w-8 h-8 rounded-lg object-cover" />}
              <span className="font-bold text-sm" style={{ fontFamily: T.font.heading }}>{agency.displayName || agency.name}</span>
            </div>
            <div className="flex items-center gap-4">
              {agency.social?.instagram && <a href={agency.social.instagram} target="_blank" rel="noopener noreferrer" style={{ color: T.muted }}><Instagram size={16} /></a>}
              <Link to="/" className="text-[10px] flex items-center gap-1" style={{ color: T.muted }}>
                Powered by <span className="font-bold" style={{ fontFamily: T.font.display }}>Auto<span style={{ color: T.cyan }}>Flow</span></span>
              </Link>
            </div>
          </div>
          <div className="text-center mt-4 text-[10px]" style={{ color: 'rgba(255,255,255,0.3)' }}>
            &copy; {new Date().getFullYear()} {agency.displayName || agency.name}. Todos los derechos reservados.
          </div>
        </div>
      </footer>

      {/* ═══ FLOATING WHATSAPP (MOBILE) ═══ */}
      {phone && (
        <a href={waLink(phone, '', '', '')} target="_blank" rel="noopener noreferrer"
          className="fixed bottom-5 right-5 z-50 md:hidden w-14 h-14 rounded-full flex items-center justify-center shadow-lg"
          style={{ background: '#25D366', boxShadow: '0 4px 20px rgba(37,211,102,0.4)' }}
          data-testid="floating-whatsapp"
        ><MessageCircle size={24} color="white" /></a>
      )}
    </div>
  );
};

export default PublicAgencyPage;
